import { isDefined } from '@sgme/fp';
import { useReadonlyField } from 'components/common-form/contexts/readonly/FieldMetadataContext';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'components/shared/Intl';
import { DealSplitIndependentAmount } from 'models/forms/DealSplitData';
import { FC } from 'react';

import type { DealSplitHeader, ProductSummaryDealSplitProps } from './DealSplitField.connect';
import { connectProductSummaryDealSplit } from './DealSplitField.connect';

export interface DealSplit {
  NotionalAmountInGrid: number;
  Custodian: string;
  IndependentAmount: DealSplitIndependentAmount;
  id: string;
}
export const Component: FC<ProductSummaryDealSplitProps> = ({ node, availableCustodian }) => {
  const metaData = node.columns as DealSplitHeader[];
  const { getData, getMetadataWithData } = useReadonlyField();
  const dealSplitData = getMetadataWithData(node);

  const getCustodianName = (custodianId: string): string => {
    const custodian = availableCustodian.find(x => x.id === custodianId);
    if (custodian === undefined) {
      return custodianId;
    } else {
      return custodian.label;
    }
  };

  const getIndependentAmountUnit = (ia: DealSplitIndependentAmount | undefined): string => {
    if (!ia) {
      return '';
    }
    if (ia.Type === 'Currency') {
      return getData('Currency') ?? '';
    } else if (ia.Type === 'Percentage') {
      return '%';
    }
    return '';
  };

  return (
    dealSplitData && (
      <table className="table table-striped">
        <thead>
          <tr>
            <th data-e2e="dealSplit-header-index">#</th>
            {metaData.map((dealSplitHead: DealSplitHeader, index: number) => {
              return (
                <th data-e2e={`dealSplit-header-${dealSplitHead.id.toLowerCase()}`} key={index}>
                  <FormattedMessage id={`grid.headers.${dealSplitHead.id}` as any} defaultMessage={dealSplitHead.id} />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {dealSplitData
            .filter((dealSplit: DealSplit) => dealSplit?.Custodian?.length > 0)
            .map((dealSplit: DealSplit, index) => {
              return (
                <tr key={`${dealSplit.id}-${index}`} className="text-regular fw-light m-0" data-e2e="dealSplit-row">
                  <td data-e2e="index">{index + 1}</td>

                  {metaData.map((cellData: DealSplitHeader, key: number) => {
                    if (cellData.id === 'Custodian') {
                      return (
                        <td data-e2e="custodian" key={`${dealSplit.id}-${index}-${key}`}>
                          {getCustodianName(dealSplit.Custodian)}
                        </td>
                      );
                    } else if (cellData.id === 'NotionalAmountInGrid') {
                      return (
                        <td data-e2e="notional" key={`${dealSplit.id}-${index}-${key}`}>
                          <>
                            <FormattedNumber value={dealSplit.NotionalAmountInGrid} />{' '}
                            {cellData.unitId === 'Units' ? 'Units' : getData('Currency')}
                          </>
                        </td>
                      );
                    } else if (cellData.id === 'IndependentAmountType') {
                      return (
                        <td data-e2e="notional" key={`${dealSplit.id}-${index}-${key}`}>
                          {dealSplit.IndependentAmount?.Type}
                        </td>
                      );
                    } else if (cellData.id === 'IndependentAmountValue') {
                      return (
                        <td data-e2e="notional" key={`${dealSplit.id}-${index}-${key}`}>
                          <>
                            <FormattedNumber value={dealSplit.IndependentAmount?.Value} />{' '}
                            {getIndependentAmountUnit(dealSplit.IndependentAmount)}
                          </>
                        </td>
                      );
                    } else if (cellData.id === 'IndependentAmountValueDate') {
                      return (
                        <td data-e2e="notional" key={`${dealSplit.id}-${index}-${key}`}>
                          <>
                            {isDefined(dealSplit.IndependentAmount?.ValueDate) && (
                              <FormattedDate
                                value={dealSplit.IndependentAmount.ValueDate ?? ''}
                                year="numeric"
                                month="short"
                                day="2-digit"
                              />
                            )}
                          </>
                        </td>
                      );
                    }
                  })}
                </tr>
              );
            })}
        </tbody>
      </table>
    )
  );
};

Component.displayName = 'ProductSummaryDealSplitField';

export const ProductSummaryDealSplitField = connectProductSummaryDealSplit(Component);
